import { useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { paths } from '@/app/router/paths';
import { useUser } from '@/features/auth/hooks/use-user';
import { User } from '@/features/auth/types/user';
import { useUserOrgs } from '@/features/org/hooks/use-user-orgs';
import { GetSubprojectInvitationResult } from '@/features/partners/api/get-subproject-invitation';
import { useGetSubprojectInvitation } from '@/features/partners/hooks/use-get-subproject-invitation';
import { useManagePartnerInvitationMutation } from '@/features/partners/hooks/use-manage-partner-invitation-mutation';
import {
  FlowLayout,
  FlowLayoutBody,
} from '@/shared/components/flow/flow-layout';
import { Background } from '@/shared/components/layouts/background';
import { LogoWithName } from '@/shared/components/theme/logo-with-name';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Spinner } from '@/shared/components/ui/spinner';
import { getInitials } from '@/shared/utils/avatar-util';

interface InvitationError {
  title: string;
  message: string;
}

interface InvitationCardProps {
  title: string;
  children: React.ReactNode;
  isError?: boolean;
}

const InvitationCard = ({
  title,
  children,
  isError = false,
}: InvitationCardProps) => {
  return (
    <Card variant={'styled'}>
      <CardHeader className="bg-muted/20">
        <CardTitle
          className={`text-center ${isError ? 'text-destructive' : ''}`}
        >
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-4 pt-6">
        {children}
      </CardContent>
    </Card>
  );
};

const ErrorCard = ({ title, message }: InvitationError) => {
  const navigate = useNavigate();
  const { t } = useTranslation('partnerInvitation');

  return (
    <InvitationCard title={title} isError>
      <p className="text-center text-destructive">{message}</p>
      <Button onClick={() => navigate(paths.home.getHref())} variant="outline">
        {t('goHome')}
      </Button>
    </InvitationCard>
  );
};

const LoadingCard = () => {
  const { t } = useTranslation('partnerInvitation');

  return (
    <Card variant={'styled'} wrapWithStars>
      <CardHeader className="items-center space-y-14 pt-14">
        <CardTitle className="text-4xl font-medium">
          {t('loadingTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-8 pb-0 pt-6">
        <Spinner className="size-12" />
      </CardContent>
    </Card>
  );
};

const Layout = ({ children }: { children: React.ReactNode }) => (
  <Background>
    <div className="fixed left-10 top-10">
      <LogoWithName />
    </div>
    <FlowLayout>
      <FlowLayoutBody>{children}</FlowLayoutBody>
    </FlowLayout>
  </Background>
);

const InvitationActions = ({
  invitation,
  user,
  invitationToken,
}: {
  invitation: GetSubprojectInvitationResult;
  user: User;
  invitationToken: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('partnerInvitation');
  const { mutate } = useManagePartnerInvitationMutation({
    onSuccess: () => navigate(paths.home.getHref()),
  });

  const handleAccept = () => {
    navigate(paths.partners.acceptInvitation.getHref(invitationToken));
  };
  const handleDecline = () => mutate({ invitationToken, status: 'declined' });

  return (
    <Card variant={'styled'} wrapWithStars>
      <CardHeader className="items-center space-y-14 pt-14">
        {invitation.project && (
          <Avatar className="size-24">
            <AvatarFallback>
              {getInitials(invitation.project?.name || '')}
            </AvatarFallback>
          </Avatar>
        )}

        <CardTitle className="text-4xl font-medium">{t('title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-8 pb-0 pt-6">
        <p className="text-center text-xl">
          <Trans
            ns="partnerInvitation"
            i18nKey="description"
            values={{
              user_name: user.user_metadata.name,
              project_name: invitation.project?.name,
            }}
          />
        </p>
        <p className="text-center text-xl">{t('prompt')}</p>
      </CardContent>
      <CardFooter className="mt-14 justify-center gap-12 pb-14">
        <Button onClick={handleDecline} variant="outline" className="w-40">
          {t('declineButton')}
        </Button>
        <Button onClick={handleAccept} variant="greenGradient" className="w-40">
          {t('acceptButton')}
        </Button>
      </CardFooter>
    </Card>
  );
};

export const ManagePartnerInvitation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('partnerInvitation');
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('token');

  const { data: user, isLoading: isUserLoading } = useUser();
  const {
    data: orgs,
    isLoading: isOrgsLoading,
    isFetching: isOrgsFetching,
  } = useUserOrgs();
  const {
    data: invitation,
    isLoading: isInvitationLoading,
    error: invitationError,
  } = useGetSubprojectInvitation({
    invitationToken: invitationToken || '',
    enabled: !!invitationToken && !!user,
  });
  const { isPending } = useManagePartnerInvitationMutation({
    onSuccess: () => navigate(paths.home.getHref()),
  });

  const nonSelfOrgs = useMemo(
    () => orgs?.filter((org) => !org.self_orga_user_id) ?? [],
    [orgs],
  );

  // Redirect to login if not logged in
  useEffect(() => {
    if (!user && !isUserLoading && invitationToken) {
      const redirectPath =
        paths.partners.manageInvitation.getHref(invitationToken);
      navigate(paths.auth.login.getHref(redirectPath));
    }
  }, [user, isUserLoading, invitationToken, navigate]);

  // Redirect to org creation if user has no real (non-self) org
  const needsOrgCreation =
    !isOrgsLoading &&
    !isOrgsFetching &&
    user &&
    orgs &&
    nonSelfOrgs.length === 0;

  useEffect(() => {
    if (needsOrgCreation) {
      const currentPath = location.pathname + location.search;
      navigate(paths.org.create.getHref(currentPath), { replace: true });
    }
  }, [needsOrgCreation, navigate, location]);

  // Redirect if invitation already processed
  useEffect(() => {
    const isProcessed =
      invitation?.status === 'accepted' || invitation?.status === 'declined';
    if (invitation && isProcessed) {
      navigate(paths.home.getHref());
    }
  }, [invitation, navigate]);

  if (!invitationToken) {
    return <ErrorCard title={t('errorTitle')} message={t('missingToken')} />;
  }

  return (
    <Layout>
      <Body
        isUserLoading={isUserLoading}
        isInvitationLoading={isInvitationLoading}
        isOrgsLoading={isOrgsLoading || isOrgsFetching}
        needsOrgCreation={!!needsOrgCreation}
        isPending={isPending}
        invitation={invitation}
        invitationError={invitationError}
        user={user}
        invitationToken={invitationToken}
      />
    </Layout>
  );
};

const Body = ({
  isUserLoading,
  isInvitationLoading,
  isOrgsLoading,
  needsOrgCreation,
  isPending,
  invitation,
  invitationError,
  user,
  invitationToken,
}: {
  isUserLoading: boolean;
  isInvitationLoading: boolean;
  isOrgsLoading: boolean;
  needsOrgCreation: boolean;
  isPending: boolean;
  invitation: GetSubprojectInvitationResult | undefined;
  invitationError: Error | null | undefined;
  user: User | null | undefined;
  invitationToken: string;
}) => {
  const { t } = useTranslation('partnerInvitation');

  if (
    isUserLoading ||
    isInvitationLoading ||
    isOrgsLoading ||
    isPending ||
    needsOrgCreation
  ) {
    return <LoadingCard />;
  }

  if (invitationError && !invitation) {
    return <ErrorCard title={t('errorTitle')} message={t('invalidToken')} />;
  }

  if (!invitation) {
    return <ErrorCard title={t('errorTitle')} message={t('fetchError')} />;
  }

  // Check if the logged-in user is the intended recipient
  if (user?.email !== invitation.to_email) {
    return <ErrorCard title={t('errorTitle')} message={t('wrongUser')} />;
  }

  return (
    <InvitationActions
      invitationToken={invitationToken}
      invitation={invitation}
      user={user}
    />
  );
};
